import { useEffect, useMemo, useState } from 'react';
import { useLocation, useRouteMatch } from 'react-router-dom';
import { Route } from '~/Constants/Routes.constants';
import { isUserConfirmingEmail, isUserVerifyingEmail } from '~/Helpers/Route.helpers';
import { useHasAccess, useNavigation, useProvider, useUser } from '~/Hooks';
import { useLocalStorage } from '~/Hooks/useLocalStorage';
import { EmailVerificationEnum } from '~/Models/DTO/User.model';

const { LOGIN, LOGIN_USC, HOME, PROVIDER_DETAIL, PROVIDER_ROOT } = Route;

export const usePrivateRoutes = () => {
  const loc = useLocation();
  const { accessToken } = useLocalStorage();
  const { hasAccess } = useHasAccess();
  const { navigate } = useNavigation();
  const { currentUser, hasUserLoaded, userError, hasRolesLoaded } = useUser();
  const {
    currentProvider,
    hasAvailableProvidersLoaded,
    availableProviders,
    isUsingThePartnerTool,
    hasCurrentProviderLoaded,
    isCurrentProviderLoading,
  } = useProvider();
  const isDomainMissing = !useRouteMatch<{ domain?: string }>({
    path: PROVIDER_DETAIL,
    location: loc,
  });

  const [logOutURL, setLogOutURL] = useState(LOGIN);

  const isLoggedIn = useMemo(() => !!accessToken, [accessToken]);
  const isOnLandingPage = useMemo(() => loc.pathname === PROVIDER_ROOT, [loc.pathname]);
  const isAccessingAPrivatePage = useMemo(
    () => loc && !hasAccess(loc.pathname) && loc.pathname !== HOME,
    [loc.pathname, hasAccess],
  );

  // Accept reject page related consts
  const matchAcceptRejectPublicAPI = useRouteMatch<{ domain?: string }>({
    path: Route.ACCEPT_REJECT_PUBLIC_API_WITH_PARAMS,
    location: loc,
  });
  const isOnAcceptRejectPage = !!matchAcceptRejectPublicAPI?.url;

  const isUserNotVerified = useMemo(
    () => !currentUser || currentUser?.isVerified === EmailVerificationEnum.NOT_VERIFIED,
    [currentUser],
  );
  const shouldGoToTheDashboard =
    currentProvider &&
    (isOnLandingPage || isAccessingAPrivatePage || isDomainMissing) &&
    !isOnAcceptRejectPage;

  const isNoProviderSelected = useMemo(
    () =>
      currentUser?.isVerified !== EmailVerificationEnum.NOT_VERIFIED &&
      !currentProvider &&
      hasAvailableProvidersLoaded &&
      availableProviders?.length > 1 &&
      !isUserVerifyingEmail() &&
      !isUserConfirmingEmail() &&
      !hasCurrentProviderLoaded &&
      !isCurrentProviderLoading,
    [
      currentUser,
      currentProvider,
      hasAvailableProvidersLoaded,
      availableProviders,
      loc.pathname,
      isUserVerifyingEmail,
      isUserConfirmingEmail,
      hasCurrentProviderLoaded,
      isCurrentProviderLoading,
    ],
  );

  useEffect(() => {
    if (currentProvider) {
      setLogOutURL(isUsingThePartnerTool ? LOGIN_USC : LOGIN);
    }
  }, [currentProvider, isUsingThePartnerTool]);

  const goBackToSafety = () => {
    navigate(PROVIDER_DETAIL, { domain: currentProvider?.domain || '' });
  };

  return {
    isLoggedIn,
    isUserNotVerified,
    isNoProviderSelected,
    logOutURL,
    hasUserLoaded,
    userError,
    goBackToSafety,
    shouldGoToTheDashboard,
    hasRolesLoaded,
  };
};
